import { Camera, Loader2, Star, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useLocation } from "react-router";

import {
  captureRegion,
  type PageRect,
  RegionSelectOverlay,
} from "@/components/region-select-overlay";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSubmitFeedback } from "@/lib/api";
import { FEEDBACK_CATEGORIES, type FeedbackCategory } from "@/lib/feedback";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

// `selecting` hides every chrome owned by this component and mounts the
// region overlay; `capturing` keeps both hidden while html-to-image runs so
// neither the floating button nor the overlay land in the snapshot.
type Mode = "idle" | "open" | "selecting" | "capturing";

/**
 * Floating feedback affordance: a vertical tab fixed to the right screen
 * edge that opens a dialog with category, optional star rating, message,
 * and an optional drag-to-select region screenshot.
 *
 * Mount once in the authenticated shell — it renders nothing until clicked.
 */
export function FeedbackButton() {
  const pathname = useLocation().pathname;
  const [mode, setMode] = useState<Mode>("idle");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [category, setCategory] = useState<FeedbackCategory>("general");
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submitFeedback = useSubmitFeedback();
  const sending = submitFeedback.isPending;

  const reset = useCallback(() => {
    setMessage("");
    setRating(null);
    setCategory("general");
    setScreenshot(null);
    setError("");
    setSuccess(false);
  }, []);

  const closeDialog = () => {
    reset();
    setMode("idle");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      return;
    }
    setError("");
    try {
      await submitFeedback.mutateAsync({
        message: message.trim(),
        rating,
        category,
        sourcePath: pathname ?? null,
        screenshotDataUrl: screenshot,
      });
      logger.debug("[feedback]", "submitted", { category, rating });
      setSuccess(true);
      setMessage("");
      setRating(null);
      setCategory("general");
      setScreenshot(null);
      setTimeout(() => setMode("idle"), 1200);
    } catch (err) {
      logger.warn("[feedback] submit failed", err);
      const detail = (err as { body?: { detail?: unknown } } | undefined)?.body?.detail;
      setError(typeof detail === "string" ? detail : "Failed to send feedback.");
    }
  };

  const handleAreaCaptured = async (rect: PageRect | null) => {
    if (!rect) {
      // User cancelled — return straight to the dialog without a shot.
      setMode("open");
      return;
    }
    setMode("capturing");
    try {
      // Wait one paint so the overlay is gone before html-to-image walks the
      // DOM. Otherwise the dimmer flashes into the snapshot.
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      const dataUrl = await captureRegion(rect);
      setScreenshot(dataUrl);
    } catch (err) {
      logger.warn("[feedback] screenshot capture failed", err);
      // Surface the size-cap error verbatim so the user knows to crop
      // smaller; everything else is collapsed to a generic message.
      const message =
        err instanceof Error && err.message.startsWith("Screenshot is too large")
          ? err.message
          : "Couldn't capture the screen. Please try again.";
      setError(message);
    } finally {
      setMode("open");
    }
  };

  return (
    <>
      {mode === "idle" && (
        <button
          type="button"
          onClick={() => setMode("open")}
          aria-label="Send feedback"
          title="Send feedback"
          className={cn(
            "fixed top-1/2 right-0 z-30 -translate-y-1/2 rotate-180 rounded-r-md px-1.5 py-1 text-xs font-medium tracking-wide shadow-lg transition-colors [writing-mode:vertical-rl]",
            "bg-primary text-primary-foreground hover:bg-primary/90",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          )}
        >
          Feedback
        </button>
      )}

      {mode === "selecting" && <RegionSelectOverlay onDone={handleAreaCaptured} />}

      <Dialog
        open={mode === "open"}
        onOpenChange={(next) => {
          if (!next) {
            closeDialog();
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-foreground text-2xl font-normal tracking-tight">
              Send feedback
            </DialogTitle>
            <DialogDescription>
              Tell us what&apos;s working, what isn&apos;t, or what you&apos;d like to see next.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-foreground text-sm font-medium" htmlFor="feedback-category">
                  Type
                </label>
                <Select
                  value={category}
                  onValueChange={(value) => setCategory(value as FeedbackCategory)}
                >
                  <SelectTrigger id="feedback-category" className="w-full">
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDBACK_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-foreground text-sm font-medium" htmlFor="feedback-rating">
                  Rating <span className="text-muted">(optional)</span>
                </label>
                <div id="feedback-rating" className="flex items-center gap-1 pt-1">
                  {[1, 2, 3, 4, 5].map((value) => {
                    const active = rating !== null && value <= rating;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setRating(rating === value ? null : value)}
                        aria-label={`${value} star${value === 1 ? "" : "s"}`}
                        className="hover:bg-warm-gray-02 rounded-md p-1 transition-colors"
                      >
                        <Star
                          className={cn(
                            "h-5 w-5",
                            active ? "fill-jj-yellow-01 text-jj-yellow-01" : "text-warm-gray-05",
                          )}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-foreground text-sm font-medium" htmlFor="feedback-message">
                Message
              </label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                maxLength={4000}
                rows={5}
                placeholder="What's on your mind?"
              />
            </div>

            <div className="space-y-2">
              <p className="text-foreground text-sm font-medium">Screenshot</p>
              {screenshot ? (
                <div className="border-border relative overflow-hidden rounded-md border">
                  <img
                    src={screenshot}
                    alt="Selected screenshot"
                    className="block max-h-48 w-full object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => setScreenshot(null)}
                    aria-label="Remove screenshot"
                    className="bg-background/90 text-foreground hover:bg-background absolute top-1 right-1 inline-flex h-7 w-7 items-center justify-center rounded-md border shadow-sm"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-center gap-2"
                  onClick={() => setMode("selecting")}
                >
                  <Camera className="h-4 w-4" />
                  Capture area of the screen
                </Button>
              )}
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
            {success && <p className="text-jj-green-03 text-sm">Thanks — feedback sent.</p>}
            <DialogFooter>
              <Button variant="outline" type="button" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={sending || !message.trim()}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send feedback"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
